/// <reference path="../../../../WebUI/typings/tsd.d.ts" />
import * as React from "react";
import * as ReactDOM from "react-dom";

import * as Rest from "../../Generated/RestClient";
import {clientAppSettings} from "../../Models/ClientAppSettings";
import {BotPageProps, BotTabs} from "./BotPage";
import { resourceStrings, FooterControl } from "../../../../Intercom.ReactUI/Views/index";
import { Environment } from "../../../../Intercom.ReactUI/Utils/Environment";
import { Resources } from "../../Resources/Intercom.Resources.DevPortal";
import { Culture } from "../../Resources/Culture";
import {ChevronDropdownControl} from "./ChevronDropdownControl";
import {
    ResponsiveContainer,
    LineChart,
    Pie,
    PieChart,
    Cell,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Brush
} from 'recharts';

const userRetentionDaysBack = 10;

const colors = [
// Primary
"#BF0077",
"#E81123",
"#F7630C",
"#FFB900",
"#BAD80A",
"#16C60C",
"#00CEA6",
"#00BCF2",
"#0046FF",
"#413EB3",
"#735BC1",
"#721481",
// Secondary
"#C30052",
"#F03A17",
"#FF8C00",
"#FCE100",
"#BAD80A",
"#00CC6A",
"#00B7C3",
"#0078D7",
"#2849EC",
"#5A4EBC",
"#5C2E91",
"#9A0089",
];

interface IChannel {
    stroke: string,
    id: string,
    displayName: string,
    iconUrl: string
}

interface IChannelCollection {
    [id: string]: IChannel;
}

class ChannelArt {
    private static _channels: IChannelCollection = {};

    private static _missing: IChannel = {
        stroke: "#000000",
        id: "<missing>",
        displayName: "<Missing>",
        iconUrl: ""
    };

    static startup() {
        clientAppSettings.channels.forEach((channel, index) => {
            this._channels[channel.Id] = {
                stroke: colors[index],
                id: channel.Id,
                displayName: channel.Name,
                iconUrl: channel.IconUrl
            };
        });
    }

    static channelCollection(): IChannelCollection {
        return this._channels;
    }

    static channelList(): IChannel[] {
        return Object.keys(this._channels).map(key => this._channels[key]);
    }

    static channel(id: string): IChannel {
        return this._channels[id] || this._missing;
    }
}

function abbreviateNumber(value: number) {
    var newValue = '' + value;
    if (value >= 1000) {
        let suffixes = ["", "k", "m", "b","t"];
        let suffixNum = Math.floor( (""+value).length/3 );
        let shortValue = 0;
        let shortNum = '';
        for (var precision = 2; precision >= 1; precision--) {
            shortValue = parseFloat( (suffixNum != 0 ? (value / Math.pow(1000,suffixNum) ) : value).toPrecision(precision));
            var dotLessShortValue = (shortValue + '').replace(/[^a-zA-Z 0-9]+/g,'');
            if (dotLessShortValue.length <= 2) { break; }
        }
        if (shortValue % 1 != 0) shortNum = shortValue.toFixed(1);
        newValue = shortValue+suffixes[suffixNum];
    }
    return newValue;
}


abstract class Observable {
    private _listeners: { (): void }[] = [];

    public subscribe(listener: () => void) {
        let isSubscribed = true;
        this._listeners.push(listener);
        const unsubscribe = () => {
            if (!isSubscribed) {
                return;
            }
            isSubscribed = false;
            const index = this._listeners.indexOf(listener);
            this._listeners.splice(index, 1);
        }
        return unsubscribe;
    }

    protected notify() {
        [...this._listeners].forEach((listener) => listener());
    }
}

interface IFilterChannel {
    visible: boolean;
    generation: number;
    channel: IChannel;
}

interface IFilterChannelCollection {
    [id: string]: IFilterChannel;
}

class ChannelFilter extends Observable {
    private _channels: IFilterChannelCollection = {};
    private _generation = 0;

    public startup() {
        if (!Object.keys(this._channels).length) {
            ChannelArt.startup();
            ChannelArt.channelList().forEach((channel, index) => {
                this._channels[channel.id] = {
                    visible: true,
                    generation: 0,
                    channel
                };
            });
        }
    }

    public get generation(): number {
        return this._generation;
    }

    public isVisible(id: string): boolean {
        return this._channels[id] && this._channels[id].visible;
    }

    public channel(id: string): IFilterChannel {
        return this._channels[id];
    }

    public setVisible(id: string, visible: boolean) {
        if (this._channels[id]) {
            if (this._channels[id].visible !== visible) {
                this._generation++;
                this._channels[id].visible = visible;
                this._channels[id].generation = this.generation;
                this.notify();
            }
        }
    }

    public toggleVisible(id: string) {
        this.setVisible(id, !this.isVisible(id));
    }

    public visibleChannelList(): IChannel[] {
        const keys = Object.keys(this._channels);
        return keys
            .sort((a, b) => this._channels[a].channel.displayName.localeCompare(this._channels[b].channel.displayName))
            .sort((a, b) => this._channels[b].generation - this._channels[a].generation)
            .filter(key => this.isVisible(key))
            .map(key => ChannelArt.channel(key));
    }
}

class TimespanSelector extends Observable {
    public static ONE_HOUR = "1-hour";
    public static ONE_DAY = "1-day";
    public static ONE_WEEK = "1-week";
    public static ONE_MONTH = "1-month";
    public static THREE_MONTHS = "3-months";

    private _timespan = TimespanSelector.ONE_MONTH;
    private _generation = 0;

    public static get timespans() {
        return [
            TimespanSelector.ONE_HOUR,
            TimespanSelector.ONE_DAY,
            TimespanSelector.ONE_WEEK,
            TimespanSelector.ONE_MONTH,
            TimespanSelector.THREE_MONTHS
        ];
    }

    public static displayNameForTimespan(timespan: string): string {
        switch (timespan) {
            case TimespanSelector.ONE_HOUR: return Resources.timespanLastHour;
            case TimespanSelector.ONE_DAY: return Resources.timespanLastDay;
            case TimespanSelector.ONE_WEEK: return Resources.timespanLastWeek;
            case TimespanSelector.ONE_MONTH: return Resources.timespanLastMonth;
            case TimespanSelector.THREE_MONTHS: return Resources.timespanLast90Days;
            default: return Resources.timespanUnknown;
        }
    }

    public static dateRangeForTimespan(timespan: string): string {
        const hour = 60 * 60 * 1000;
        const day = 24 * hour;
        let now = Date.now();
        let then: number;
        switch (timespan) {
            case TimespanSelector.ONE_HOUR: then = now - hour; break;
            case TimespanSelector.ONE_DAY: then = now - day; break;
            case TimespanSelector.ONE_WEEK: then = now - day * 7; break;
            case TimespanSelector.ONE_MONTH: then = now - day * 31; break;
            case TimespanSelector.THREE_MONTHS: then = now - day * 90; break;
            default: return Resources.timespanUnknown;
        }
        return `${new Date(then).toLocaleDateString(Culture.CultureCode)} - ${new Date(now).toLocaleDateString(Culture.CultureCode)}`;
    }

    public startup() {
    }

    public get timespan() {
        return this._timespan;
    }

    public get generation() {
        return this._generation;
    }

    public set timespan(value: string) {
        this._timespan = value;
        this._generation++;
        this.notify();
    }

    // Assumption: AppInsights always sends date/time in this format
    datePattern = /(\d+)\/(\d+)\/(\d+)/;
    timePattern = /(\d+):(\d+):(\d+)\s+(AM|PM)/;

    public labelForAppInsightsDate(s: string): string {
        const dateMatch = s.match(this.datePattern);
        const timeMatch = s.match(this.timePattern);
        let month = Number(dateMatch[1]);
        let day = Number(dateMatch[2]);
        let year = Number(dateMatch[3]);
        let hour = Number(timeMatch[1]);
        let minute = Number(timeMatch[2]);
        let period = timeMatch[4];
        month -= 1;
        hour %= 12;
        if (period === 'PM')
            hour += 12;
        // Date components are in UTC, but when creating a Date object from them, they are
        // misinterpreted as being in local time. This misinterpretation erroneously adds
        // the timezone offset to the value. Solution is to create a second date object,
        // subtracting the offset out.
        let date = new Date(year, month, day, hour, minute);
        let local = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
        switch (this.timespan) {
            case TimespanSelector.ONE_HOUR:
            case TimespanSelector.ONE_DAY:
                return local.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
            case TimespanSelector.ONE_WEEK:
            case TimespanSelector.ONE_MONTH:
            case TimespanSelector.THREE_MONTHS:
                return local.toLocaleDateString(Culture.CultureCode);
        }
    }
}

abstract class DataStore<StateT> extends Observable {
    protected _dataset: StateT;
    protected _error: any;
    protected _generation = 0;
    protected _isLoading: boolean = true;

    constructor(protected _botId: string, protected _method: (botId: string, analyticsRequest: Rest._AnalyticsRequest) => JQueryPromise<StateT>) {
        super();
    }

    public get dataset() {
        return this._dataset;
    }
    public get error() {
        return this._error;
    }
    public get generation() {
        return this._generation;
    }
    public get isLoading() {
        return this._isLoading;
    }

    abstract timespan(): string;
    abstract massage(data: StateT): StateT;

    public refresh() {
        this._method(this._botId, { Timespan: this.timespan() })
            .then((data: StateT) => {
                this._dataset = this.massage(data);
                this._error = null;
                this._generation++;
                this._isLoading = false;
                this.notify();
            })
            .fail((error) => {
                this._dataset = null;
                this._error = error;
                this._generation++;
                this._isLoading = false;
                this.notify();
            });
    }

    // TODO: Check for localization
    public getErrorText() {
        let parts = [];
        if (this.error) {
            if (this.error.status) {
                parts.push(this.error.status);
            } else if (this.error.statusText) {
                parts.push(this.error.statusText);
            }
            if (this.error.responseJSON) {
                parts.push(this.error.responseJSON);
            }
        }
        return parts.join(' - ');
    }
}

class TimelineDataStore extends DataStore<Rest.AnalyticsNumericTimelineResponse> {
    subscriptions = [];

    constructor(botId: string, method: (botId: string, analyticsRequest: Rest._AnalyticsRequest) => JQueryPromise<Rest.AnalyticsNumericTimelineResponse>) {
        super(botId, method);
    }

    public get channels() {
        return this._dataset ? ChannelArt.channelList()
            .filter((channel) => this._dataset.Totals.hasOwnProperty(channel.id))
            .filter((channel) => AnalyticsStore.channelFilter.isVisible(channel.id))
            : [];
    }

    public startup() {
        if (!this.subscriptions.length) {
            this.subscriptions.push(AnalyticsStore.timespanSelector.subscribe(() => this.timespanChanged()));
        }
        if (!this.dataset) {
            this.refresh();
        }
    }

    public timespanChanged() {
        this.refresh();
    }

    timespan(): string {
        return AnalyticsStore.timespanSelector.timespan;
    }

    massage(data: Rest.AnalyticsNumericTimelineResponse): Rest.AnalyticsNumericTimelineResponse {
        return data;
    }
}

class UserTimelineDataStore extends TimelineDataStore {
    constructor(botId: string) {
        super(botId, Rest.AnalyticsClient.getUsers);
    }
}

class MessageTimelineDataStore extends TimelineDataStore {
    constructor(botId: string) {
        super(botId, Rest.AnalyticsClient.getMessages);
    }
}

class UserRetentionDataStore extends DataStore<Rest.AnalyticsUserRetentionResponse> {
    subscriptions = [];

    constructor(botId: string) {
        super(botId, Rest.AnalyticsClient.getUserRetention);
    }

    public startup() {
        if (!this.subscriptions.length) {
            //this.subscriptions.push(AnalyticsStore.timespanSelector.subscribe(() => this.timespanChanged()));
        }
        if (!this.dataset) {
            this.refresh();
        }
    }

    public timespanChanged() {
        this.refresh();
    }

    timespan(): string {
        return (userRetentionDaysBack + 1).toString();
    }

    massage(data: Rest.AnalyticsUserRetentionResponse): Rest.AnalyticsUserRetentionResponse {
        return data;
    }
}

class AnalyticsStore {
    public static botId: string;
    public static channelFilter: ChannelFilter;
    public static timespanSelector: TimespanSelector;
    public static userTimelineDataStore: UserTimelineDataStore;
    public static messageTimelineDataStore: MessageTimelineDataStore;
    public static userRetentionDataStore: UserRetentionDataStore;

    public static bootstrap(botId: string) {
        if (this.botId != botId) {
            this.botId = botId;
            this.channelFilter = new ChannelFilter();
            this.timespanSelector = new TimespanSelector();
            this.userTimelineDataStore = new UserTimelineDataStore(botId);
            this.messageTimelineDataStore = new MessageTimelineDataStore(botId);
            this.userRetentionDataStore = new UserRetentionDataStore(botId);
        }
        // Order of startup is important here.
        this.channelFilter.startup();
        this.timespanSelector.startup();
        this.userTimelineDataStore.startup();
        this.messageTimelineDataStore.startup();
        this.userRetentionDataStore.startup();
    }
}

export interface AnalyticsProps extends React.Props<any> {
    bot: Rest.BotService;
    showWebChat: boolean;
    tabClickHandler: (tab: BotTabs) => void;
}

export class AnalyticsDashboard extends React.Component<AnalyticsProps, {}> {
    timespanSelector: TimespanSelectorControl;

    constructor(props) {
        super(props);
        // If app insights isn't set up don't bother initializing the analytics store.
        if (this.props.bot.DeveloperAppInsightsAPIKey && this.props.bot.DeveloperAppInsightsApplicationId) {
            AnalyticsStore.bootstrap(props.bot.Id);
        }
    }

    renderHeader(): JSX.Element {
        return (
            <div className="analytics-header">
                <div className="analytics-controls">
                    <div className="flex-spacer"/>
                    <ChannelFilterControl />
                    <TimespanSelectorControl ref={x => this.timespanSelector = x} />
                </div>
            </div>
        );
    }

    render(): JSX.Element {
        if (this.props.bot.DeveloperAppInsightsAPIKey && this.props.bot.DeveloperAppInsightsApplicationId) {
            return (
                <div className="analytics-dashboard">
                    {this.renderHeader()}
                    <div className="analytics-body">
                        <GrandTotalsCard />
                        <UserRetentionTable
                            dataStore={AnalyticsStore.userRetentionDataStore}
                            title={Resources.retentionTitle}
                            desc={Resources.retentionDescription} />
                        <TimelineCard
                            dataStore={AnalyticsStore.userTimelineDataStore}
                            title={Resources.timelineCardUsersTitle}  />
                        <TimelineCard
                            dataStore={AnalyticsStore.messageTimelineDataStore}
                            title={Resources.timelineCardMessagesTitle} />
                        <FooterControl scrollingClass="analytics-body" />
                    </div>
                </div>
            );
        } else {
            const notConfiguredSubText = Environment.isInBotFrameworkAzureExtension ? (
                <p>{Resources.analyticsNotConfiguredActionIbiza}</p>
            ) : (
                <p>{Resources.analyticsNotConfiguredAction}<a href="javascript:void(0)" onClick={() => this.props.tabClickHandler(BotTabs.Settings)}>{resourceStrings.v4.botPageTabs[BotTabs.Settings]}</a></p>
            );

            return (
                <div className="analytics-dashboard">
                    <div className="analytics-body no-header">
                        <div className="description-card-wrapper">
                            <div className="description-card-contents">
                                <h2>{Resources.analyticsNotConfigured}</h2>
                                {notConfiguredSubText}
                                <p><a href="//aka.ms/bf-analytics" target="_blank">{Resources.learnMore}</a></p>
                            </div>
                        </div>
                        <FooterControl scrollingClass="analytics-body" />
                    </div>
                </div>
            );
        }
    }
}


class ChannelFilterControl extends React.Component<{}, {}> {
    subscriptions = [];
    checkboxRefs = {};

    componentDidMount() {
        this.subscriptions.push(AnalyticsStore.channelFilter.subscribe(() => this.channelsUpdated()));
    }
    componentWillUnmount() {
        this.subscriptions.forEach((unsubscribe) => unsubscribe());
        this.subscriptions = [];
    }
    channelsUpdated() {
        this.forceUpdate();
    }

    onChannelClick(el: React.MouseEvent<any>, id: string) {
        AnalyticsStore.channelFilter.toggleVisible(id);
        this.checkboxRefs[id].checked = AnalyticsStore.channelFilter.isVisible(id);
    }

    labelChannelStyle: React.CSSProperties = {
        padding: "0 8px 0 8px"
    };

    renderLabelChannel(channel: IChannel): JSX.Element {
        return (
            <div key={`${channel.id}-${AnalyticsStore.channelFilter.generation}`} style={this.labelChannelStyle} className="channel-filter-channel-id">
                <span style={{color: channel.stroke}}>{'\u25CF'}</span><span>{'\u00A0'}{channel.displayName}</span>
            </div>
        );
    }

    renderLabelEllipsis(): JSX.Element {
        return (
            <div key="ellipsis" style={this.labelChannelStyle}>
                <span>{'\u2026'}</span>
            </div>
        );
    }

    renderPickerLabel(): JSX.Element {
        let channels = AnalyticsStore.channelFilter.visibleChannelList();
        let top = channels.slice(0, 4);
        let items = top.map(channel => this.renderLabelChannel(channel))
        if (channels.length > top.length) {
            items.push(this.renderLabelEllipsis());
        }
        let style: React.CSSProperties = {
            whiteSpace: "nowrap",
            display: "flex"
        };
        return (
            <div key={AnalyticsStore.channelFilter.generation} style={style}>{items}</div>
        );
    }

    renderPickerCell(channel: IChannel): JSX.Element {
        return (
            <td key={channel.id}>
                <label className="m-checkbox-container">
                    <input
                        type="checkbox"
                        className="m-checkbox"
                        ref={ref => this.checkboxRefs[channel.id] = ref}
                        role="checkbox"
                        onClick={(el) => this.onChannelClick(el, channel.id)}
                        aria-checked={AnalyticsStore.channelFilter.isVisible(channel.id)}
                        defaultChecked={AnalyticsStore.channelFilter.isVisible(channel.id)} />
                    <span className="m-icon-Checkbox unchecked"></span>
                    <span className="m-icon-CheckboxComposite checked"></span>
                    {channel.displayName}
                </label>
            </td>
        )
    }

    renderPickerRow(channels: IChannel[], index: number): JSX.Element {
        let cells = channels.splice(0, 2).map(channel => this.renderPickerCell(channel));
        return (<tr key={index}>{cells}</tr>);
    }

    renderPickerContent(): JSX.Element {
        let channels = ChannelArt.channelList();
        let rows: JSX.Element[] = [];
        let index = 0;
        while (channels.length) {
            rows.push(this.renderPickerRow(channels, index++))
        }
        return (
            <div className="channel-filter">
                <table><tbody>{rows}</tbody></table>
            </div>
        );
    }

    render(): JSX.Element {
        return (
            <ChevronDropdownControl
                className="channel-filter-dropdown"
                label={this.renderPickerLabel()}
                content={this.renderPickerContent()}
                buttonHiddenLabel={ Resources.analyticsChannelsDropdown }
            />
        )
    }
}

class TimespanSelectorControl extends React.Component<any, null> {
    control: ChevronDropdownControl;
    subscriptions = [];
    checkboxRefs = {};

    componentDidMount() {
        this.subscriptions.push(AnalyticsStore.timespanSelector.subscribe(() => this.timespanUpdated()));
    }
    componentWillUnmount() {
        this.subscriptions.forEach((unsubscribe) => unsubscribe());
        this.subscriptions = [];
    }
    timespanUpdated() {
        this.forceUpdate();
    }

    public close() {
        if (this.control) {
            this.control.close();
        }
    }

    onClicked(timespan: string) {
        AnalyticsStore.timespanSelector.timespan = timespan;
        this.checkboxRefs[timespan].checked = AnalyticsStore.timespanSelector.timespan === timespan;
    }

    renderPickerLabel(): JSX.Element {
        return (
            <div className="timespan-label" key={"timespan-label" + AnalyticsStore.timespanSelector.generation}>{TimespanSelector.dateRangeForTimespan(AnalyticsStore.timespanSelector.timespan)}</div>
        );

    }

    renderPickerContent(): JSX.Element {
        let selectors = TimespanSelector.timespans.map((timespan) => {
            return (
                <div key={timespan}>
                    <label className="m-radio-container">
                        <input className="m-radio"
                            type="radio"
                            name="timespan"
                            onClick={() => this.onClicked(timespan)}
                            ref={ref => this.checkboxRefs[timespan] = ref}
                            role="radio"
                            aria-checked={AnalyticsStore.timespanSelector.timespan === timespan}
                            defaultChecked={AnalyticsStore.timespanSelector.timespan === timespan} />
                        <span className="m-icon-RadioBtnOff unchecked"></span>
                        <span className="m-icon-RadioBtnOn checked"></span>
                        {TimespanSelector.displayNameForTimespan(timespan)}
                    </label>
                </div>
            );
        });
        return (
            <div className="timespan-selector">
                <form>
                    {selectors}
                </form>
            </div>
        );
    }

    render(): JSX.Element {
        return (
            <ChevronDropdownControl
                ref={x => this.control = x}
                className="timespan-picker-dropdown"
                label={this.renderPickerLabel()}
                content={this.renderPickerContent()}
                buttonHiddenLabel={ Resources.analyticsPeriodDropdown }
            />
        );
    }
}

class GrandTotalsCard extends React.Component<{}, {}> {
    subscriptions = [];

    componentDidMount() {
        this.subscriptions.push(AnalyticsStore.channelFilter.subscribe(() => this.storeUpdated()));
        this.subscriptions.push(AnalyticsStore.userTimelineDataStore.subscribe(() => this.storeUpdated()));
        this.subscriptions.push(AnalyticsStore.messageTimelineDataStore.subscribe(() => this.storeUpdated()));
    }
    componentWillUnmount() {
        this.subscriptions.forEach((unsubscribe) => unsubscribe());
        this.subscriptions = [];
    }
    storeUpdated() {
        this.forceUpdate();
    }

    getGrandTotal(datastore: TimelineDataStore): number {
        if (datastore.dataset) {
            let total = 0;
            let lines = datastore.channels
                .map((channel) => {
                    total += datastore.dataset.Totals[channel.id];
                });
            return total;
        }
        else {
            return null;
        }
    }

    render(): JSX.Element {
        return (
            <div className="analytics-card totals-card">
                <h3>{Resources.totalsCardTitle}</h3>
                <TotalCard title={Resources.timelineCardUsersTitle} total={this.getGrandTotal(AnalyticsStore.userTimelineDataStore)} />
                <TotalCard title={Resources.timelineCardMessagesTitle} total={this.getGrandTotal(AnalyticsStore.messageTimelineDataStore)} />
            </div>
        );
    }
}


interface TotalCardProps extends React.Props<TimelineCard> {
    title: string;
    total: number;
    color?: string;
    disabled?: boolean;
}

class TotalCard extends React.Component<TotalCardProps, {}> {
    render(): JSX.Element {
        let color = this.props.color || "transparent";
        let disabled = this.props.disabled || false;
        let value = this.props.total ? abbreviateNumber(this.props.total) : '--';
        return (
            <div className={"total-card" + (disabled ? " disabled" : "")}>
                <div aria-hidden="true" className="total-card-title" style={{ borderBottomColor: color }}>
                    <div className="total-card-title-text">{value}</div>
                </div>
                <div aria-label={(value === '--' ? Resources.undetermined : value) + " " +this.props.title}>
                    <div aria-hidden="true">{this.props.title}</div>
                    <div className="offscreen">{(value === '--' ? Resources.undetermined : value) + " " +this.props.title}</div>                
                </div>
            </div>
        );
    }
}


interface TimelineCardProps extends React.Props<TimelineCard> {
    dataStore: TimelineDataStore;
    title: string;
}

class TimelineCard extends React.Component<TimelineCardProps, {}> {
    subscriptions = [];

    componentDidMount() {
        this.subscriptions.push(AnalyticsStore.channelFilter.subscribe(() => this.channelsUpdated()));
        this.subscriptions.push(this.props.dataStore.subscribe(() => this.storeUpdated()));
    }
    componentWillUnmount() {
        this.subscriptions.forEach((unsubscribe) => unsubscribe());
        this.subscriptions = [];
    }

    storeUpdated() {
        this.forceUpdate();
    }

    channelsUpdated() {
        this.forceUpdate();
    }

    render(): JSX.Element {
        const XAxisLabel = (props): JSX.Element => {
            if (props.payload.index > 0)
                return (
                    <g transform={"translate(" + props.x + "," + props.y + ")"}>
                        <text
                            x={0}
                            y={0}
                            dy={16}
                            textAnchor={props.textAnchor}
                            transform={"rotate(" + (props.angle || 0) + ")"}>{AnalyticsStore.timespanSelector.labelForAppInsightsDate(props.payload.value)}
                        </text>
                    </g>
                );
            return null;
        };

        if (this.props.dataStore.dataset) {
            const dataset = this.props.dataStore.dataset;
            const channels = this.props.dataStore.channels.sort((a, b) => AnalyticsStore.channelFilter.channel(a.id).generation - AnalyticsStore.channelFilter.channel(b.id).generation);

            const RADIAN = Math.PI / 180;
            const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                let radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                radius -= 5;
                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                const y = cy + radius * Math.sin(-midAngle * RADIAN);

                return (
                    <text key={index} x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                        {`${(percent * 100).toFixed(0)}%`}
                    </text>
                );
            };

            let lines = channels.map((channel) => {
                let strokeWidth = 3;
                return <Line
                    key={channel.id}
                    type="monotone"
                    stroke={ChannelArt.channel(channel.id).stroke}
                    strokeWidth={strokeWidth}
                    isAnimationActive={true}
                    dot={{r: 4}}
                    activeDot={{r: 6}}
                    dataKey={channel.id} />
            });
            let piedata = channels.map((channel) => {
                return {
                    name: channel.displayName,
                    value: dataset.Totals[channel.id]
                }
            });
            let piecolors = channels.map((channel) => {
                return <Cell key={channel.id} fill={ChannelArt.channel(channel.id).stroke} />;
            });
            let totals = channels.map((channel) => {
                return <TotalCard key={channel.id} total={this.props.dataStore.dataset.Totals[channel.id]} title={channel.displayName} color={ChannelArt.channel(channel.id).stroke} />;
            });

            return (
                <div className="analytics-card timeline-card">
                    <div className="timeline-charts">
                        <div className="timeline-chart">
                            <h3>{this.props.title}</h3>
                            <ul className="offscreen">
                                {
                                    piedata.map((row) => <li key={ row.name }>{ row.name }: { row.value }</li>)
                                }
                            </ul>
                            <div className="timeline-chart-pie">
                                <PieChart width={200} height={200}>
                                    <Pie
                                        data={piedata}
                                        cx={100}
                                        cy={100}
                                        labelLine={false}
                                        outerRadius={90}
                                        innerRadius={55}
                                        isAnimationActive={false}>
                                        {piecolors}
                                    </Pie>
                                </PieChart>
                            </div>
                        </div>
                        <div className="timeline-chart">
                            <table className="offscreen">
                                <caption>
                                    <div>
                                        <h3>{Resources.timelineTableLabel.replace('%1', this.props.title)}</h3>
                                    </div>
                                </caption>
                                <thead>
                                    <tr>
                                        <td aria-hidden='true' />
                                        {
                                            channels.map(channel => <th key={ channel.id } scope='col'>{ channel.displayName }</th>)
                                        }
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        dataset.Timeline.map(time =>
                                            <tr key={ time.Timestamp }>
                                                <th scope='row'>{ time.Timestamp }</th>
                                                {
                                                    channels.map(channel =>
                                                        <td key={ channel.id }>{ time[channel.id] }</td>
                                                    )
                                                }
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                            <div className="timeline-chart-graph">
                                <LineChart key={`chart-${this.props.dataStore.generation}`} width={760} height={300} data={dataset.Timeline} margin={{ top: 5, right: 30, left: 20, bottom: 5 }} aria-label={Resources.timelineTableLabel.replace('%1', this.props.title)}>
                                    <XAxis dataKey="Timestamp" tick={<XAxisLabel/>} />
                                    <YAxis />
                                    <CartesianGrid strokeDasharray="3 3" />
                                    {lines}
                                </LineChart>
                            </div>
                        </div>
                    </div>
                    <div className="totals-card" aria-hidden={ true }>
                        {totals}
                    </div>
                </div>
            );
        }
        else if (this.props.dataStore.error) {
            return (
                <div className="analytics-card">
                    <h3>{Resources.cardTitleError.replace('%1', this.props.title).replace('%2', this.props.dataStore.getErrorText())}</h3>
                </div>
            );
        }
        else if (!this.props.dataStore.isLoading) {
            return (
                <div className="analytics-card">
                    <h3>{Resources.cardTitleLoading.replace('%1', this.props.title).replace('%2', Resources.noAnalyticsData)}</h3>
                </div>
            );
        }
        else {
            return (
                <div className="analytics-card">
                    <h3>{Resources.cardTitleLoading.replace('%1', this.props.title).replace('%2', Resources.loading)}</h3>
                </div>
            );
        }
    }
}

interface UserRetentionTableProps extends React.Props<UserRetentionTable> {
    dataStore: UserRetentionDataStore;
    title: string;
    desc: string;
}

class UserRetentionTable extends React.Component<UserRetentionTableProps, {}> {
    subscriptions = [];

    componentDidMount() {
        this.subscriptions.push(AnalyticsStore.channelFilter.subscribe(() => this.channelsUpdated()));
        this.subscriptions.push(this.props.dataStore.subscribe(() => this.storeUpdated()));
    }
    componentWillUnmount() {
        this.subscriptions.forEach((unsubscribe) => unsubscribe());
        this.subscriptions = [];
    }

    storeUpdated() {
        this.forceUpdate();
    }
    channelsUpdated() {
        this.forceUpdate();
    }

    render(): JSX.Element {
        if (this.props.dataStore.dataset) {
            const dataset = this.props.dataStore.dataset;

            const sumWithChannels = function(data, channels: IChannel[]):number {
                let sum = 0;
                for (let channel of channels) {
                    if (data[channel.id])
                        sum += data[channel.id];
                }
                return sum;
            }

            const renderRow = function (rowData):JSX.Element {
                if (rowData.length < 1) {
                    return <tr className="Empty row">No data</tr>
                }
                let channels = (rowData.DaysData) ? ChannelArt.channelList()
                    .filter((channel) => rowData.DaysData.hasOwnProperty(channel.id))
                    .filter((channel) => AnalyticsStore.channelFilter.isVisible(channel.id))
                    : [];
                let total = sumWithChannels(rowData.DaysData, channels);
                let date = rowData.Date;
                const colorLower = [229, 244, 254]; // color lowerbound: #e5f4fe, rgb(229, 244, 254)
                const colorUpper = [25, 157, 246]; // color upperbound: #199df6, rgb(25, 157, 246)

                let percentCells = rowData.Timeline.map((entry, i) => {
                    let percent = total >= 1 ? sumWithChannels(entry, channels) / total : 0;
                    let colorRgb = colorLower.map((lower, i) => {
                        let upper = colorUpper[i];
                        return Math.floor((lower - upper) * (1-percent) + upper);
                    });
                    let colorStr = "(" + colorRgb[0] + ", " + colorRgb[1] + ", " + colorRgb[2] + ")";
                    percent = percent * 100;
                    return <td className="percent-cell" key={date + i} style={{backgroundColor: 'rgb' + colorStr}}>
                        <div className="cell-contents">{percent.toFixed(0)}%</div>
                    </td>;
                });

                return <tr key={date}>
                    <th scope="row" className="date-cell"><div>{(new Date(date).toLocaleDateString(Culture.CultureCode))}</div></th>
                    <td className="total-cell"><div>{total}</div></td>
                    {percentCells}
                </tr>
            }

            let rows = dataset.Days.map((day, index) => {
                return renderRow(day);
            });

            let dayHeaders = [];
            for (let i = 1; i <= userRetentionDaysBack; i++) {
                dayHeaders.push(<th scope="col" key={i}>
                    {i}
                </th>);
            }

            return <div className="analytics-card user-retention-card">               
                <table className="user-retention-table">
                    <caption>
                        <div>
                            <h3>{this.props.title} - {this.props.desc}</h3>
                        </div>
                    </caption>
                    <thead>
                        <tr>
                            <td className="header-corner" colSpan={2} aria-hidden="true"></td>
                            <th scope="col" className="header-days" colSpan={userRetentionDaysBack}>{Resources.retentionHeader}</th>
                        </tr>
                        <tr  className="table-header">
                            <th scope="col" className="date-cell">{Resources.retentionDate}</th>
                            <th scope="col" className="total-cell">{Resources.retentionUsers}</th>
                            {dayHeaders}
                        </tr>
                    </thead>
                    <tbody>
                        {rows}
                    </tbody>
                </table>
            </div>
        }
        else if (this.props.dataStore.error) {
            return (
                <div className="analytics-card">
                    <h3>{Resources.cardTitleError.replace('%1', this.props.title).replace('%2', this.props.dataStore.getErrorText())}</h3>
                </div>
            );
        }
        else if (!this.props.dataStore.isLoading) {
            return (
                <div className="analytics-card">
                    <h3>{Resources.cardTitleLoading.replace('%1', this.props.title).replace('%2', Resources.noAnalyticsData)}</h3>
                </div>
            );
        }
        else {
            return (
                <div className="analytics-card">
                    <h3>{Resources.cardTitleLoading.replace('%1', this.props.title).replace('%2', Resources.loading)}</h3>
                </div>
            );
        }
    }
}

interface ErrorsCardProps extends React.Props<UserRetentionTable> {
    title: string;
}

class ErrorsCard extends  React.Component<ErrorsCardProps, {}> {
    render(): JSX.Element {
        return (
            <div className="analytics-card errors-card">
                <h3>{this.props.title}</h3>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./Client/v4/Views/AnalyticsDashboard.tsx